import { createSelector } from 'reselect';

import { selectCharactersByIdentifiers } from '@lib/core/characters/selectors';
import {
  TUserBestMatchCharacterByProductCategory,
  TUserCharacterArraysByProductCategory,
} from '@lib/core/characters/utils/parseCharacter';
import { TProductCategory } from '@lib/core/products/types';
import { AppStateType } from '@lib/core/service/types/appStateType';
import { IUserCharacterSlice } from '@lib/core/users/slices/characters';
import { RouteUtils } from '@lib/tools/routes';

/**
 * @returns the whole user characters state
 */
export const selectUserCharactersState = (state): IUserCharacterSlice => state.users.characters;

/**
 * @returns user characters extended with full character data
 */
export const selectUserCharacters = createSelector(
  [selectUserCharactersState, selectCharactersByIdentifiers],
  (userCharactersState, charactersByIdentifiers) => {
    return userCharactersState?.data?.length
      ? userCharactersState?.data
          .map(({ character }) => charactersByIdentifiers[character?.identifier])
          .filter(character => !!character?.characterId)
      : [];
  },
);

/**
 * @returns array of all user characters Ids
 */
export const selectUserCharactersIds = createSelector([selectUserCharactersState], userCharactersState =>
  userCharactersState?.data.length ? userCharactersState?.data.map(({ character }) => character?.identifier) : [],
);

/**
 * @returns user characters for category
 */
export const selectUserCharacterArraysByProductCategory = createSelector([selectUserCharacters], userCharactersArray =>
  userCharactersArray.reduce((acc, characterData) => {
    const category = characterData?.characterProductCategory?.name;
    if (!category) return acc;
    if (!acc[category]) acc[category] = [];
    acc[category].push(characterData);
    return acc;
  }, {} as TUserCharacterArraysByProductCategory),
);

/**
 * @returns user characters for service product category array
 */
export const selectUserCharactersForServiceProductCategory = createSelector(
  [selectUserCharacters, (state: AppStateType) => state?.service?.productCategory || RouteUtils.getProductCategory()],
  (userCharactersArray, productCategory) =>
    userCharactersArray.filter(character => character.characterProductCategory?.name === productCategory),
);

/**
 * @returns user best matches character for category
 */
export const selectUserBestMatchCharacterByProductCategory = createSelector(
  [selectUserCharacterArraysByProductCategory],
  (
    userCharactersByCategory: ReturnType<typeof selectUserCharacterArraysByProductCategory>,
  ): TUserBestMatchCharacterByProductCategory => {
    return Object.entries(userCharactersByCategory).reduce((acc, [category, characters]) => {
      acc[category as TProductCategory] = characters[characters.length - 1];
      return acc;
    }, {} as TUserBestMatchCharacterByProductCategory);
  },
);

/**
 * @returns user best matches characters identifiers as single string (useful for useEffect)
 */
export const selectUserBestMatchCharacterByProductCategoryIdentifiers = createSelector(
  [selectUserBestMatchCharacterByProductCategory],
  (userBestMatchCharacterByProductCategory: ReturnType<typeof selectUserBestMatchCharacterByProductCategory>): string =>
    Object.values(userBestMatchCharacterByProductCategory)
      .map(character => character?.characterId)
      .filter(Boolean)
      .join(','),
);

/**
 * @returns if user has any character (completed at least 1 test)
 */
export const selectIsUserHasAnyCharacter = createSelector(
  [selectUserCharacters],
  userCharacters => !!userCharacters.length,
);

/**
 * @returns if user has character for retailer where we have only 1 product type
 */
export const selectIsUserHasCharacterForCurrentProductCategory = createSelector(
  [
    selectUserBestMatchCharacterByProductCategory,
    (state: AppStateType) => state?.service?.productCategory || RouteUtils.getProductCategory(),
  ],
  (
    userBestMatchCharacterByProductCategory: ReturnType<typeof selectUserBestMatchCharacterByProductCategory>,
    productCategory,
  ) => {
    return !!userBestMatchCharacterByProductCategory[productCategory];
  },
);

/**
 * @returns if user characters are loading
 */
export const selectIsCharactersAreLoading = createSelector(
  [selectUserCharactersState],
  userCharactersState => !!userCharactersState.isLoading,
);

/**
 * @returns user characters for current product category
 */
export const selectBestMatchCharacterForServiceProductCategory = createSelector(
  [
    selectUserBestMatchCharacterByProductCategory,
    (state: AppStateType) => state?.service?.productCategory || RouteUtils.getProductCategory(),
  ],
  (
    userBestMatchCharacterByProductCategory: ReturnType<typeof selectUserBestMatchCharacterByProductCategory>,
    productCategory,
  ) => userBestMatchCharacterByProductCategory[productCategory],
);
